/**
 * CategoriesPanel — Fix 6
 *
 * Manages shop categories (shop_categories table).
 * Rendered inside ShopAdminPage under the "Categories" tab (index 2).
 *
 * API contracts (all MADE-182 / MADE-265):
 *   GET    /api/madebb/shop-admin/shop-categories   → [{ id, name, slug, sort_order, product_count }]
 *   POST   /api/madebb/shop-admin/shop-categories   → { name }
 *   PUT    /api/madebb/shop-admin/shop-categories/:id → { name, sort_order }
 *   DELETE /api/madebb/shop-admin/shop-categories/:id → 409 if products assigned
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { authFetch } from '../utils/authFetch';

interface ShopCategory {
  id: number;
  name: string;
  slug: string;
  sort_order: number;
  product_count: number;
}

const API = '/api/madebb/shop-admin/shop-categories';

export function CategoriesPanel() {
  const [categories, setCategories]     = useState<ShopCategory[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [successMsg, setSuccessMsg]     = useState<string | null>(null);

  // Add / edit dialog
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [editTarget, setEditTarget]     = useState<ShopCategory | null>(null); // null = add
  const [formName, setFormName]         = useState('');
  const [formSortOrder, setFormSortOrder] = useState('0');
  const [formSaving, setFormSaving]     = useState(false);
  const [formError, setFormError]       = useState<string | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<ShopCategory | null>(null);
  const [deleting, setDeleting]         = useState(false);
  const [deleteError, setDeleteError]   = useState<string | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(API);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setCategories(data.categories ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // ── Add / edit dialog ─────────────────────────────────────────────────────

  const openAdd = () => {
    setEditTarget(null);
    setFormName('');
    setFormSortOrder(String((categories.length > 0 ? Math.max(...categories.map(c => c.sort_order)) : 0) + 1));
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (cat: ShopCategory) => {
    setEditTarget(cat);
    setFormName(cat.name);
    setFormSortOrder(String(cat.sort_order));
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (formSaving) return;
    setDialogOpen(false);
    setEditTarget(null);
    setFormName('');
    setFormError(null);
  };

  const handleSave = async () => {
    if (!formName.trim()) { setFormError('Name is required'); return; }
    setFormSaving(true);
    setFormError(null);
    try {
      const payload = { name: formName.trim(), sort_order: parseInt(formSortOrder) || 0 };
      let res: Response;
      if (editTarget) {
        res = await authFetch(`${API}/${editTarget.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await authFetch(API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error((d as { error?: string }).error ?? `Server returned ${res.status}`);
      }
      showSuccess(editTarget ? `"${formName}" updated` : `"${formName}" created`);
      setDialogOpen(false);
      loadCategories();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setFormSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await authFetch(`${API}/${deleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error((d as { error?: string }).error ?? `Server returned ${res.status}`);
      }
      showSuccess(`"${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      loadCategories();
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete category');
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box data-testid="categories-panel">
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Shop Categories</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadCategories}
          disabled={loading}
          data-testid="categories-refresh-btn"
        >
          Refresh
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          data-testid="add-category-btn"
        >
          Add Category
        </Button>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="categories-error">{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} data-testid="categories-success">{successMsg}</Alert>}

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table data-testid="categories-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell align="right">Sort</TableCell>
                <TableCell align="right">Products</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No categories yet. Click "Add Category" to create one.
                  </TableCell>
                </TableRow>
              )}
              {categories.map(cat => (
                <TableRow key={cat.id} data-testid={`category-row-${cat.id}`}>
                  <TableCell sx={{ fontWeight: 600 }}>{cat.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{cat.slug}</TableCell>
                  <TableCell align="right">{cat.sort_order}</TableCell>
                  <TableCell align="right">{cat.product_count ?? 0}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => openEdit(cat)}
                      data-testid={`edit-category-${cat.id}`}
                      aria-label={`Edit ${cat.name}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <Tooltip
                      title={cat.product_count > 0
                        ? `${cat.product_count} product${cat.product_count !== 1 ? 's' : ''} assigned — unassign first`
                        : 'Delete category'}
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={cat.product_count > 0}
                          onClick={() => { setDeleteTarget(cat); setDeleteError(null); }}
                          data-testid={`delete-category-${cat.id}`}
                          aria-label={`Delete ${cat.name}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editTarget ? `Edit "${editTarget.name}"` : 'Add Category'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            required
            value={formName}
            onChange={e => setFormName(e.target.value)}
            autoFocus
            sx={{ mt: 1, mb: 2 }}
            inputProps={{ 'data-testid': 'category-name-input' }}
            error={!!formError}
            helperText={formError}
          />
          <TextField
            label="Sort order"
            type="number"
            size="small"
            value={formSortOrder}
            onChange={e => setFormSortOrder(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{ width: 120 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Slug is auto-generated from name (lowercase, hyphens).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={formSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={formSaving}
            data-testid="save-category-dialog-btn"
          >
            {formSaving ? 'Saving…' : editTarget ? 'Save changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete category?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
            This cannot be undone.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="delete-category-error">
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            data-testid="confirm-delete-category-btn"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
